import React, { useEffect, useRef, useState, ReactElement } from "react";
import { useAuth, useUser } from "@/app/providers";
import { Navigate, Outlet } from "react-router";
import { AppBar, Box, Grid, Link, Toolbar } from "@mui/material";
import { styled } from "styled-components";
import { PartnerImagePath } from "@/hooks";
import { queries } from "@/graphql";
import { getPartnerKey } from "@/utils";
import { LanguageSelector } from "@/features";
import { useLazyQuery } from "@apollo/client/react";

const LogoImage = styled("img")(() => ({
  width: "auto",
  maxHeight: "40px",
}));

/**
 * Authentication Layout Component
 */
export const AuthLayout = (): ReactElement => {
  const { isAuthenticated } = useAuth();
  const appbarRef = useRef<HTMLDivElement>(null);
  const [logo, setLogo] = useState<string | undefined>();
  const [companyUrl, setCompanyUrl] = useState<string>("/");
  const partnerKey = getPartnerKey();
  const { user } = useUser();
  const [getPartners] = useLazyQuery(queries.getAllPartners);

  useEffect(() => {
    let isMounted = true;

    PartnerImagePath(partnerKey, "logos/logo-dark.png").then(
      (imageUrl: string) => {
        if (isMounted) {
          setLogo(imageUrl);
        }
      },
    );

    return () => {
      isMounted = false;
    };
  }, [partnerKey]);

  useEffect(() => {
    let isMounted = true;

    getPartners()
      .then((res: any) => {
        if (!isMounted) {
          return;
        }

        const partners = res?.data?.getPartners || [];
        const selectedPartner = partners.find(
          (partner: any) => partner?.key === partnerKey,
        );

        setCompanyUrl(selectedPartner?.companyUrl || "/");
      })
      .catch(() => {
        if (isMounted) {
          setCompanyUrl("/");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [getPartners, partnerKey]);

  return !isAuthenticated || !user ? (
    <>
      <Box
        sx={{
          minHeight: "100%",
          overflow: "hidden",
        }}
      >
        <AppBar ref={appbarRef} position="static">
          <Toolbar>
            <Box
              sx={{
                maxHeight: "60px",
              }}
            >
              <Link
                href={companyUrl || "/"}
                sx={{
                  lineHeight: "90px",
                }}
              >
                {logo && <LogoImage alt="logo" src={logo} />}
              </Link>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
              }}
            />
            <Box>
              <LanguageSelector />
            </Box>
          </Toolbar>
        </AppBar>

        <Grid container sx={{ width: "100%" }}>
          <Outlet />
        </Grid>
        <footer></footer>
      </Box>
    </>
  ) : (
    <Navigate to="/" />
  );
};
